import type { ActionMotionIntent, ActionPhase, ActionTransitionSeed, ActiveAction, Vec3 } from "../core";

export interface RotationChannels {
  x: number;
  y: number;
  z: number;
}

export interface ProceduralActionPose {
  root: Vec3;
  /** Actor-local wrist target used by the shared analytic arm solver. */
  activeHandTarget: Vec3;
  /** Actor-local endpoint target for the active weapon or guard. */
  weaponEndpointTarget: Vec3;
  /** Actor-local ankle targets consumed by the humanoid rig solver. */
  leftFootTarget: Vec3;
  rightFootTarget: Vec3;
  torso: RotationChannels;
  head: RotationChannels;
  leftArm: RotationChannels;
  rightArm: RotationChannels;
  leftForearm: RotationChannels;
  rightForearm: RotationChannels;
  leftHand: RotationChannels;
  rightHand: RotationChannels;
  leftLeg: RotationChannels;
  rightLeg: RotationChannels;
  leftShin: RotationChannels;
  rightShin: RotationChannels;
  leftFoot: RotationChannels;
  rightFoot: RotationChannels;
  weapon: RotationChannels;
  guard: RotationChannels;
}

export const ACTION_MOTION_PRESETS = {
  cleave: {
    hand: "right", delivery: "swing", plane: "vertical", direction: 1,
    energy: 0.88, reach: 0.76, advance: 0.55, guard: 0.72, targetHeight: 0.56,
  },
  sweep: {
    hand: "right", delivery: "swing", plane: "horizontal", direction: -1,
    energy: 0.74, reach: 0.9, advance: 0.36, guard: 0.58, targetHeight: 0.48,
  },
  thrust: {
    hand: "right", delivery: "thrust", plane: "vertical", direction: 1,
    energy: 0.82, reach: 1, advance: 0.82, guard: 0.42, targetHeight: 0.62,
  },
  shieldBash: {
    hand: "left", delivery: "bash", plane: "horizontal", direction: 1,
    energy: 0.9, reach: 0.48, advance: 0.76, guard: 1, targetHeight: 0.58,
  },
  risingSlash: {
    hand: "right", delivery: "swing", plane: "diagonal", direction: -1,
    energy: 0.8, reach: 0.82, advance: 0.46, guard: 0.52, targetHeight: 0.82,
  },
} as const satisfies Record<string, ActionMotionIntent>;

export function inferActionMotionIntent(action?: Pick<ActiveAction, "action" | "tags" | "motion"> | null): ActionMotionIntent {
  if (action?.motion) return normalizeIntent(action.motion);
  const tags = action?.tags ?? [];
  const label = action?.action.toLowerCase() ?? "";
  if (tags.includes("shield") || label.includes("guard") || label.includes("bash")) {
    return { ...ACTION_MOTION_PRESETS.shieldBash };
  }
  if (tags.includes("projectile") || tags.includes("spell") || label.includes("thrust")) {
    return { ...ACTION_MOTION_PRESETS.thrust, advance: tags.includes("spell") ? 0.12 : ACTION_MOTION_PRESETS.thrust.advance };
  }
  return { ...ACTION_MOTION_PRESETS.cleave };
}

export function sampleActionMotionPolicy(
  intentInput: ActionMotionIntent,
  phase: ActionPhase,
  phaseProgress: number,
  transitionFrom?: ActionTransitionSeed,
): ProceduralActionPose {
  const { intent, entryPose } = resolveActionEntry(intentInput, transitionFrom);
  return sampleResolvedActionMotionPolicy(intent, phase, phaseProgress, entryPose);
}

/**
 * Collapse the current generated action into a fresh, bounded seed. This keeps
 * long combos constant-size: a source records the policy-resolved direction,
 * not an ever-growing linked list of previous actions.
 */
export function createActionTransitionSeed(
  intentInput: ActionMotionIntent,
  phase: ActionPhase,
  phaseProgress: number,
  transitionFrom?: ActionTransitionSeed,
): ActionTransitionSeed {
  const { intent } = resolveActionEntry(intentInput, transitionFrom);
  return { motion: intent, phase, phaseProgress: clamp01(phaseProgress) };
}

function resolveActionEntry(
  intentInput: ActionMotionIntent,
  transitionFrom?: ActionTransitionSeed,
): { intent: ActionMotionIntent; entryPose?: ProceduralActionPose } {
  const requestedIntent = normalizeIntent(intentInput);
  const entryPose = transitionFrom
    ? sampleResolvedActionMotionPolicy(
      normalizeIntent(transitionFrom.motion),
      transitionFrom.phase,
      transitionFrom.phaseProgress,
    )
    : undefined;
  return {
    intent: entryPose ? resolveChainedIntent(requestedIntent, entryPose) : requestedIntent,
    entryPose,
  };
}

function sampleResolvedActionMotionPolicy(
  intent: ActionMotionIntent,
  phase: ActionPhase,
  phaseProgress: number,
  entryPose?: ProceduralActionPose,
): ProceduralActionPose {
  const neutral = neutralPose(intent);
  const authoredWindup = windupPose(intent);
  const contact = contactPose(intent);
  const follow = followPose(intent, contact);
  applyActionTargetKeyframes(intent, neutral, authoredWindup, contact, follow);
  // A chained telegraph adds only a small load toward the authored windup.
  // The sword stays on its current side instead of visibly resetting through
  // idle before the next commit begins.
  const windup = entryPose
    ? mixPose(entryPose, authoredWindup, intent.delivery === "swing" ? 0.22 : 0.34)
    : authoredWindup;
  const p = clamp01(phaseProgress);

  let result: ProceduralActionPose;
  if (phase === "telegraph") result = mixPose(entryPose ?? neutral, windup, easeOutCubic(p));
  else if (phase === "commit") {
    const pose = mixPose(windup, contact, smoothstep(p));
    if (intent.delivery === "swing") {
      // Euler interpolation alone drops the active arm through the neutral
      // silhouette between anticipation and contact. A shared side arc keeps
      // the hand travelling around the body and rushes through that weak pose.
      const arc = Math.sin(p * Math.PI) * (0.42 + intent.energy * 0.5);
      const active = activeChannels(pose, intent.hand);
      active.arm.z -= intent.direction * arc;
      active.forearm.x -= arc * 0.18;
      // Keep the wrist loaded through the middle of the cut. A plain Euler
      // blend passes both hand and grip through their neutral rotations here,
      // which visually welds the sword to the forearm at the fastest point.
      const plane = planeWeights(intent.plane);
      active.hand.x -= (0.14 + plane.vertical * 0.2) * arc;
      active.hand.y -= intent.direction * (0.18 + plane.horizontal * 0.18) * arc;
      active.hand.z -= intent.direction * (0.1 + plane.horizontal * 0.12) * arc;
      pose.weapon.x -= (0.1 + plane.vertical * 0.14) * arc;
      pose.weapon.y -= intent.direction * plane.horizontal * 0.16 * arc;
      pose.weapon.z -= intent.direction * 0.12 * arc;
      pose.torso.x -= arc * 0.07;
      const targetArc = Math.sin(p * Math.PI) * (0.05 + intent.energy * 0.1);
      pose.activeHandTarget.x += intent.direction * plane.horizontal * targetArc;
      pose.activeHandTarget.y += plane.vertical * targetArc;
    }
    result = pose;
  }
  else if (phase === "impact") {
    // Hold the contact silhouette briefly before releasing into follow-through.
    const release = p < 0.46 ? 0 : smoothstep((p - 0.46) / 0.54);
    result = mixPose(contact, follow, release);
  }
  else result = mixPose(follow, neutral, smoothstep(p));

  return result;
}

/**
 * Choose the swing direction whose contact lies across the body from the
 * current weapon endpoint. Direction is presentation morphology, so adapting
 * it here preserves gameplay authority while preventing repeated same-side
 * attacks from snapping back to their default windup.
 */
function resolveChainedIntent(
  requested: ActionMotionIntent,
  entryPose: ProceduralActionPose,
): ActionMotionIntent {
  if (requested.delivery !== "swing") return requested;
  const candidates = [-1, 1] as const;
  let resolved = requested;
  let greatestTravel = -1;
  for (const direction of candidates) {
    const candidate = { ...requested, direction };
    const neutral = neutralPose(candidate);
    const windup = windupPose(candidate);
    const contact = contactPose(candidate);
    const follow = followPose(candidate, contact);
    applyActionTargetKeyframes(candidate, neutral, windup, contact, follow);
    const travel = pointDistanceSquared(entryPose.weaponEndpointTarget, contact.weaponEndpointTarget);
    if (travel > greatestTravel) {
      greatestTravel = travel;
      resolved = candidate;
    }
  }
  return resolved;
}

function normalizeIntent(intent: ActionMotionIntent): ActionMotionIntent {
  return {
    ...intent,
    direction: intent.direction < 0 ? -1 : 1,
    energy: clamp01(intent.energy),
    reach: clamp01(intent.reach),
    advance: clamp01(intent.advance),
    guard: clamp01(intent.guard),
    targetHeight: clamp01(intent.targetHeight),
  };
}

function emptyPose(): ProceduralActionPose {
  const rotation = (): RotationChannels => ({ x: 0, y: 0, z: 0 });
  return {
    root: { x: 0, y: 0, z: 0 },
    activeHandTarget: { x: 0.34, y: 1.58, z: 0.14 },
    weaponEndpointTarget: { x: 0.34, y: 1.58, z: 0.56 },
    leftFootTarget: { x: -0.16, y: 0.09, z: 0 },
    rightFootTarget: { x: 0.16, y: 0.09, z: 0 },
    torso: rotation(), head: rotation(),
    leftArm: rotation(), rightArm: rotation(),
    leftForearm: rotation(), rightForearm: rotation(),
    leftHand: rotation(), rightHand: rotation(),
    leftLeg: rotation(), rightLeg: rotation(),
    leftShin: rotation(), rightShin: rotation(),
    leftFoot: rotation(), rightFoot: rotation(),
    // A sword grip is not perfectly collinear with the forearm at rest. This
    // small equipment-independent cock makes the wrist joint legible while
    // leaving each equipment recipe free to author its own geometry axis.
    weapon: { x: -0.16, y: 0, z: 0.08 }, guard: rotation(),
  };
}

/** Populate target keyframes before the same pose mixer handles every phase. */
function applyActionTargetKeyframes(
  intent: ActionMotionIntent,
  neutralPoseValue: ProceduralActionPose,
  windupPoseValue: ProceduralActionPose,
  contactPoseValue: ProceduralActionPose,
  followPoseValue: ProceduralActionPose,
): void {
  const side = intent.hand === "right" ? 1 : -1;
  const neutral = intent.delivery === "bash"
    ? { x: side * 0.63, y: 1.95, z: 0.77 }
    : { x: side * 0.32, y: 1.28, z: -0.02 };
  const neutralEndpoint = intent.delivery === "bash"
    ? { x: side * 0.63, y: 1.95, z: 0.8 }
    : { x: side * 0.1, y: -0.18, z: 0.42 };
  const reach = intent.reach;
  let windup: Vec3;
  let contact: Vec3;
  let follow: Vec3;
  let windupEndpoint: Vec3;
  let contactEndpoint: Vec3;
  let followEndpoint: Vec3;

  // These are true trajectory families, not variants of one forward reach.
  // The analytic arm solver owns contact accuracy, so the targets themselves
  // must carry the readable path: high-to-low, side-to-side, low-to-high, or
  // straight through. Otherwise every authored joint pose is collapsed into
  // the same visible "poke" during the final constraint pass.
  if (intent.delivery === "thrust") {
    windup = { x: side * 0.4, y: 1.48, z: -0.08 };
    contact = { x: side * 0.34, y: 1.7, z: 0.58 + reach * 0.18 };
    follow = { x: side * 0.31, y: 1.66, z: 0.54 + reach * 0.18 };
    windupEndpoint = { x: side * 0.36, y: 1.47, z: 0.38 };
    contactEndpoint = { x: side * 0.31, y: 1.69, z: 1.14 + reach * 0.3 };
    followEndpoint = { x: side * 0.28, y: 1.65, z: 1.06 + reach * 0.28 };
  } else if (intent.delivery === "bash") {
    windup = { x: side * 0.58, y: 1.92, z: 0.08 };
    contact = { x: side * 0.26, y: 1.78, z: 0.52 + reach * 0.12 };
    follow = { x: side * 0.18, y: 1.72, z: 0.56 + reach * 0.1 };
    windupEndpoint = { x: side * 0.5, y: 1.93, z: 0.42 };
    contactEndpoint = { x: side * 0.06, y: 1.79, z: 0.9 + reach * 0.16 };
    followEndpoint = { x: -side * 0.02, y: 1.73, z: 0.92 + reach * 0.14 };
  } else if (intent.plane === "horizontal") {
    windup = { x: side * 0.34 - intent.direction * 0.28, y: 1.62, z: 0.04 };
    contact = { x: side * 0.14 + intent.direction * 0.26, y: 1.6, z: 0.4 + reach * 0.06 };
    follow = { x: side * 0.14 + intent.direction * 0.24, y: 1.58, z: 0.34 + reach * 0.05 };
    windupEndpoint = { x: windup.x - intent.direction * 0.34, y: 1.65, z: 0.4 };
    contactEndpoint = { x: contact.x + intent.direction * 0.44, y: 1.58, z: 0.92 + reach * 0.12 };
    followEndpoint = { x: follow.x + intent.direction * 0.5, y: 1.48, z: 0.82 + reach * 0.1 };
  } else if (intent.plane === "diagonal") {
    windup = { x: side * 0.34 - intent.direction * 0.22, y: 1.28, z: 0.02 };
    contact = { x: side * 0.12 + intent.direction * 0.2, y: 1.88 + intent.targetHeight * 0.08, z: 0.42 + reach * 0.06 };
    follow = { x: side * 0.1 + intent.direction * 0.28, y: 2.0, z: 0.34 + reach * 0.04 };
    windupEndpoint = { x: windup.x - intent.direction * 0.28, y: 0.92, z: 0.36 };
    contactEndpoint = { x: contact.x + intent.direction * 0.34, y: 2.22, z: 0.94 + reach * 0.08 };
    followEndpoint = { x: follow.x + intent.direction * 0.4, y: 2.3, z: 0.82 + reach * 0.06 };
  } else {
    windup = { x: side * 0.28, y: 2.1, z: -0.16 };
    contact = { x: side * 0.24, y: 1.48 - intent.targetHeight * 0.08, z: 0.42 + reach * 0.08 };
    follow = { x: side * 0.2, y: 1.42, z: 0.32 + reach * 0.06 };
    windupEndpoint = { x: side * 0.2, y: 2.52, z: 0.16 };
    contactEndpoint = { x: side * 0.2, y: 0.98, z: 0.82 + reach * 0.12 };
    followEndpoint = { x: side * 0.16, y: 0.9, z: 0.62 + reach * 0.1 };
  }
  neutralPoseValue.activeHandTarget = neutral;
  windupPoseValue.activeHandTarget = windup;
  contactPoseValue.activeHandTarget = contact;
  followPoseValue.activeHandTarget = follow;
  neutralPoseValue.weaponEndpointTarget = neutralEndpoint;
  windupPoseValue.weaponEndpointTarget = windupEndpoint;
  contactPoseValue.weaponEndpointTarget = contactEndpoint;
  followPoseValue.weaponEndpointTarget = followEndpoint;
}

function pointDistanceSquared(a: Vec3, b: Vec3): number {
  return (a.x - b.x) ** 2 + (a.y - b.y) ** 2 + (a.z - b.z) ** 2;
}

function activeChannels(pose: ProceduralActionPose, hand: "left" | "right") {
  return hand === "left"
    ? { arm: pose.leftArm, forearm: pose.leftForearm, hand: pose.leftHand }
    : { arm: pose.rightArm, forearm: pose.rightForearm, hand: pose.rightHand };
}

function passiveChannels(pose: ProceduralActionPose, hand: "left" | "right") {
  return hand === "left"
    ? { arm: pose.rightArm, forearm: pose.rightForearm, hand: pose.rightHand }
    : { arm: pose.leftArm, forearm: pose.leftForearm, hand: pose.leftHand };
}

function applyGuard(pose: ProceduralActionPose, intent: ActionMotionIntent): void {
  const passive = passiveChannels(pose, intent.hand);
  const side = intent.hand === "right" ? -1 : 1;
  passive.arm.x = -0.25 - intent.guard * 0.7;
  passive.arm.z = side * (0.08 + intent.guard * 0.28);
  passive.forearm.x = -0.2 - intent.guard * 0.92;
  passive.hand.z = side * intent.guard * 0.1;
}

function neutralPose(intent: ActionMotionIntent): ProceduralActionPose {
  const pose = emptyPose();
  applyGuard(pose, intent);
  applyStance(pose, intent, 0.04, -0.04);
  return pose;
}

function applyStance(
  pose: ProceduralActionPose,
  intent: ActionMotionIntent,
  leadZ: number,
  rearZ: number,
  width = 0.16,
): void {
  pose.leftFootTarget.x = -width;
  pose.rightFootTarget.x = width;
  const lead = intent.hand === "right" ? pose.leftFootTarget : pose.rightFootTarget;
  const rear = intent.hand === "right" ? pose.rightFootTarget : pose.leftFootTarget;
  lead.z = leadZ;
  rear.z = rearZ;
}

function planeWeights(plane: ActionMotionIntent["plane"]): { vertical: number; horizontal: number } {
  if (plane === "vertical") return { vertical: 1, horizontal: 0 };
  if (plane === "horizontal") return { vertical: 0, horizontal: 1 };
  return { vertical: 0.55, horizontal: 0.55 };
}

function windupPose(intent: ActionMotionIntent): ProceduralActionPose {
  const pose = neutralPose(intent);
  const active = activeChannels(pose, intent.hand);
  const { vertical, horizontal } = planeWeights(intent.plane);
  const direction = intent.direction;
  const energy = 0.65 + intent.energy * 0.55;

  // Canonical humanoids face local +Z. Because limbs are authored down -Y,
  // positive X pulls them backward toward -Z and negative X drives forward.
  // Windup intentionally loads behind the body before contact reverses it.
  active.arm.x = intent.delivery === "thrust" ? 0.52 : intent.delivery === "bash" ? 0.4 : 0.42 + vertical * 0.74;
  active.arm.z = -direction * (0.28 + horizontal * 0.86) * energy;
  active.forearm.x = intent.delivery === "bash" ? 0.76 : 0.88 + vertical * 0.18;
  active.forearm.y = direction * horizontal * 0.2;
  active.hand.x = intent.delivery === "thrust" ? 0.16 : -0.16 - vertical * 0.18;
  active.hand.y = direction * (horizontal * 0.28 + vertical * 0.08);
  active.hand.z = -direction * (0.16 + horizontal * 0.12);
  pose.torso.y = -direction * (0.22 + intent.energy * 0.3);
  pose.torso.x = intent.delivery === "thrust" ? -0.08 : 0;
  pose.torso.z = direction * horizontal * 0.06;
  pose.head.y = -pose.torso.y * 0.42;
  pose.root.x = -direction * horizontal * (0.025 + intent.energy * 0.025);
  pose.root.z = -0.05 * intent.advance;
  pose.root.y = -0.035 - 0.03 * intent.energy;
  applyStance(pose, intent, 0.08 + intent.advance * 0.05, -0.08 - intent.advance * 0.1, 0.18 + horizontal * 0.05);
  pose.leftFoot.y = direction * horizontal * 0.08;
  pose.rightFoot.y = direction * horizontal * 0.08;
  pose.weapon.x = -0.26 - vertical * 0.18 + (intent.delivery === "thrust" ? 0.22 : 0);
  pose.weapon.y = direction * horizontal * 0.16;
  pose.weapon.z = -direction * (0.08 + horizontal * 0.2);
  if (intent.delivery === "bash") {
    pose.weapon = { x: -0.22, y: 0.08 * direction, z: 0.14 * direction };
  }
  applyGuard(pose, intent);
  return pose;
}

function contactPose(intent: ActionMotionIntent): ProceduralActionPose {
  const pose = neutralPose(intent);
  const active = activeChannels(pose, intent.hand);
  const { vertical, horizontal } = planeWeights(intent.plane);
  const direction = intent.direction;
  const energy = 0.7 + intent.energy * 0.6;

  if (intent.delivery === "thrust") {
    active.arm.x = -1.05 - intent.reach * 0.42;
    active.arm.z = direction * 0.08;
    active.forearm.x = 0.14;
    active.forearm.y = direction * 0.08;
    active.hand.x = -0.12;
    active.hand.y = -direction * 0.1;
    active.hand.z = direction * 0.05;
    pose.torso.x = 0.12 + intent.reach * 0.16;
  } else if (intent.delivery === "bash") {
    active.arm.x = -1.12 - intent.reach * 0.26;
    active.arm.z = direction * 0.18;
    active.forearm.x = 0.34;
    active.hand.x = -0.18;
    active.hand.y = direction * 0.12;
    pose.guard.x = -0.08;
  } else {
    active.arm.x = -0.52 - vertical * (0.68 + intent.targetHeight * 0.36) - intent.reach * 0.16;
    active.arm.z = direction * (0.18 + horizontal * 0.72) * energy;
    active.forearm.x = 0.18 + (1 - intent.reach) * 0.32;
    // Keep the blade descending through a vertical strike instead of merely
    // extending the whole arm like a thrust. Diagonal swings inherit a
    // partial wrist break from their blended plane weights.
    active.hand.x = vertical * (0.38 + intent.energy * 0.2) - horizontal * 0.16;
    active.hand.y = -direction * (horizontal * 0.34 + vertical * 0.1);
    active.hand.z = direction * (0.16 + horizontal * 0.16);
  }

  pose.torso.y = direction * (0.3 + intent.energy * 0.42);
  pose.torso.z = -direction * horizontal * (0.08 + intent.energy * 0.06);
  pose.head.y = -pose.torso.y * 0.32;
  pose.root.x = direction * horizontal * (0.06 + intent.energy * 0.05);
  pose.root.z = intent.advance * (0.18 + intent.reach * 0.18);
  // Power is produced by dropping into the stance, not rising onto two rigid
  // legs. The planted-foot solve turns this center-of-mass drop into visible
  // hip, knee, and ankle articulation.
  pose.root.y = -0.09 - 0.05 * intent.energy;
  const lunge = intent.delivery === "thrust" || intent.delivery === "bash" ? 0.12 : 0;
  applyStance(pose, intent, 0.16 + intent.advance * 0.2 + lunge, -0.06, 0.19 + horizontal * 0.07);
  pose.leftFoot.y = direction * horizontal * 0.16;
  pose.rightFoot.y = direction * horizontal * 0.16;
  pose.weapon.x = -0.1 + vertical * 0.22 - horizontal * 0.08;
  pose.weapon.y = -direction * horizontal * 0.22;
  pose.weapon.z = direction * (horizontal * 0.2 - vertical * 0.08);
  if (intent.delivery === "bash") {
    pose.weapon = { x: -0.18, y: -0.1 * direction, z: 0.12 * direction };
  }
  applyGuard(pose, intent);
  return pose;
}

function followPose(intent: ActionMotionIntent, contact: ProceduralActionPose): ProceduralActionPose {
  const pose = clonePose(contact);
  const active = activeChannels(pose, intent.hand);
  const direction = intent.direction;
  active.arm.x -= intent.delivery === "thrust" ? 0.12 : 0.28 * intent.energy;
  active.arm.z += direction * 0.18 * intent.energy;
  active.forearm.x -= 0.14;
  active.forearm.y -= direction * 0.08;
  active.hand.x += intent.delivery === "thrust" ? 0.08 : 0.22 * intent.energy;
  active.hand.y += direction * (intent.delivery === "swing" ? 0.2 : 0.06);
  active.hand.z += direction * 0.1 * intent.energy;
  pose.torso.y += direction * 0.16 * intent.energy;
  pose.torso.x += intent.delivery === "bash" ? 0.08 : -0.06;
  pose.head.y = -pose.torso.y * 0.28;
  pose.root.z += 0.06 * intent.advance;
  pose.root.x += direction * planeWeights(intent.plane).horizontal * 0.035;
  pose.root.y = -0.06 - 0.035 * intent.energy;
  const followWidth = 0.2 + planeWeights(intent.plane).horizontal * 0.07;
  pose.leftFootTarget.x = -followWidth;
  pose.rightFootTarget.x = followWidth;
  const lead = intent.hand === "right" ? pose.leftFootTarget : pose.rightFootTarget;
  lead.z += 0.04;
  pose.weapon.x += intent.delivery === "swing" ? 0.18 * intent.energy : 0.05;
  pose.weapon.y += direction * 0.1;
  pose.weapon.z += direction * 0.16;
  return pose;
}

function clonePose(pose: ProceduralActionPose): ProceduralActionPose {
  return Object.fromEntries(Object.entries(pose).map(([key, value]) => [key, { ...value }])) as unknown as ProceduralActionPose;
}

function mixPose(from: ProceduralActionPose, to: ProceduralActionPose, amount: number): ProceduralActionPose {
  const t = clamp01(amount);
  const result = emptyPose();
  for (const key of Object.keys(result) as Array<keyof ProceduralActionPose>) {
    const a = from[key];
    const b = to[key];
    (result[key] as RotationChannels).x = a.x + (b.x - a.x) * t;
    (result[key] as RotationChannels).y = a.y + (b.y - a.y) * t;
    (result[key] as RotationChannels).z = a.z + (b.z - a.z) * t;
  }
  return result;
}

function clamp01(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
}

function smoothstep(value: number): number {
  const t = clamp01(value);
  return t * t * (3 - 2 * t);
}

function easeOutCubic(value: number): number {
  const t = clamp01(value);
  return 1 - (1 - t) ** 3;
}
